/**
 * Resolve Year / Event / event_type / crew from API maneuver rows and optional table cache rows
 * so map points and time-series samples carry the same metadata as the maneuvers table.
 */

export type ManeuverLikeRow = Record<string, unknown>;

function pickFirst<T>(...vals: (T | undefined | null)[]): T | undefined {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

export function resolveYearValue(row: ManeuverLikeRow | undefined | null): string | number | undefined {
  if (!row) return undefined;
  return pickFirst(
    row.year as string | number | undefined,
    row.Year as string | number | undefined,
    row.YEAR as string | number | undefined
  );
}

export function resolveEventLabelValue(row: ManeuverLikeRow | undefined | null): string | number | undefined {
  if (!row) return undefined;
  return pickFirst(
    row.event as string | number | undefined,
    row.Event as string | number | undefined,
    row.EVENT as string | number | undefined,
    row.event_name as string | number | undefined,
    row.Event_name as string | number | undefined,
    row.EVENT_NAME as string | number | undefined
  );
}

export function resolveEventTypeValue(row: ManeuverLikeRow | undefined | null): string | number | undefined {
  if (!row) return undefined;
  return pickFirst(
    row.event_type as string | number | undefined,
    row.Event_type as string | number | undefined,
    row.EVENT_TYPE as string | number | undefined
  );
}

/** Crew code as stored on maneuver rows (`CREW_CODE` from the API, or the table's Crew field). */
export function resolveCrewValue(row: ManeuverLikeRow | undefined | null): string | number | undefined {
  if (!row) return undefined;
  return pickFirst(
    row.crew as string | number | undefined,
    row.Crew as string | number | undefined,
    row.CREW as string | number | undefined,
    row.CREW_CODE as string | number | undefined,
    row.crew_code as string | number | undefined
  );
}

/** Test comment as stored on maneuver rows (`TEST` from the API as Tests; NONE when uncovered). */
export function resolveTestsValue(row: ManeuverLikeRow | undefined | null): string | number | undefined {
  if (!row) return undefined;
  return pickFirst(
    row.tests as string | number | undefined,
    row.Tests as string | number | undefined,
    row.TESTS as string | number | undefined,
    row.TEST as string | number | undefined
  ) ?? 'NONE';
}

/** Merge API row with table row (table wins on conflicts). */
export function mergeManeuverRowWithTable(
  apiRow: ManeuverLikeRow | undefined | null,
  tableRow: ManeuverLikeRow | undefined | null
): ManeuverLikeRow {
  return { ...(apiRow ?? {}), ...(tableRow ?? {}) };
}

/**
 * Copy year / event / event_type / crew onto a track or TS point so buildColorGrouping / getGroupKeyFromItem match the table.
 * Map and timeseries queries return Crew on the event row; the drawn points were only keeping year and event, so Color By CREW painted every series with the empty-group color.
 */
export function assignYearEventMetadataOnPoint(
  point: Record<string, unknown>,
  apiRow: ManeuverLikeRow | undefined | null,
  tableRow: ManeuverLikeRow | undefined | null
): void {
  const merged = mergeManeuverRowWithTable(apiRow, tableRow);
  const y = resolveYearValue(merged);
  if (y !== undefined) {
    point.year = y;
    point.Year = y;
    point.YEAR = y;
  }
  const ev = resolveEventLabelValue(merged);
  if (ev !== undefined) {
    point.event = ev;
    point.Event = ev;
    point.EVENT = ev;
  }
  const et = resolveEventTypeValue(merged);
  if (et !== undefined) {
    point.event_type = et;
  }
  const crew = resolveCrewValue(merged);
  if (crew !== undefined) {
    point.crew = crew;
    point.Crew = crew;
    point.CREW = crew;
    point.CREW_CODE = crew;
  }
  const tests = resolveTestsValue(merged);
  if (tests !== undefined) {
    point.tests = tests;
    point.Tests = tests;
    point.TESTS = tests;
  }
}
